import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { createSearchParams, useNavigate } from 'react-router-dom'
import { Avatar, Box, Fade } from '@mui/material'
import Button from '@mui/material/Button'
import CustomPageNavigator from '@/components/Pagination'
import DataUnavailable from '@/components/DataUnavailable'
import Loader from '@/components/Loader'
import TaskModal from '@/components/Modal'
import {
  registeredConsumersForBabysitterIdResponse,
  UserProfileInterface,
} from '@/services/types/APIResponseType'
import usePostConsumersForBabysitter from '@/services/hooks/usePostConsumersForBabysitter'
import Path from '@/router/localRoutes'
import { formatPhoneNumber, stringAvatar } from '@/utils'
import close_icon from '@/assets/close_icon.svg'
import flip from '@/assets/flip_v.svg'
import './ConsumersForBabysitter.css'

interface ConsumersPropsType {
  babysitterId: string
  onClickBack: (showConsumers: boolean) => void
}

const ConsumersForBabysitter: React.FC<ConsumersPropsType> = ({
  babysitterId,
  onClickBack,
}) => {
  const itemsPerPage = 5
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: 'calc(100% - 24px)', sm: '90%', md: '668px' },
    maxWidth: '668px',
    height: { xs: 'auto', md: '520px' },
    maxHeight: '90vh',
    overflow: 'auto',
    color: 'white',
    border: '1px solid rgb(75, 90, 135)',
    borderRadius: '20px',
    background: 'rgba(69, 68, 68,0)',
    boxShadow: 24,
    backdropFilter: 'blur(12px)',
    p: { xs: 2, md: 3 },
  }

  const [isFlipped, setIsFlipped] = useState(false)
  const [open, setOpen] = React.useState<boolean>(false)
  const [consumerData, setConsumerData] =
    useState<registeredConsumersForBabysitterIdResponse>()
  const [consumerProfilesData, setConsumerProfilesData] = useState<
    (UserProfileInterface & { babysitterId: string })[]
  >([])
  const [currentPage, setCurrentPage] = useState<number>(1)

  const {
    mutate: consumersForBabySitter,
    isPending,
    isSuccess,
  } = usePostConsumersForBabysitter()

  const navigate = useNavigate()
  const currentItems = consumerProfilesData
  const requestedData = useMemo(
    () => ({
      babysitterUserId: babysitterId,
    }),
    [babysitterId],
  )

  const handleFlip = () => {
    setIsFlipped(!isFlipped)
  }

  const handleBack = () => {
    onClickBack(false)
  }

  const handleViewProof = () => {
    setOpen(true)
  }

  const handleViewNavigate = (consumerID: string) => {
    navigate({
      pathname: Path.RegisteredConsumerDetails,
      search: createSearchParams({
        from: 'baby-sitter',
        consumerID,
      }).toString(),
    })
  }

  const getConsumerData = useCallback(() => {
    consumersForBabySitter(
      {
        limit: itemsPerPage,
        page: currentPage,
        payload: requestedData,
      },
      {
        onSuccess: (response) => {
          setConsumerData(response.data)
          setConsumerProfilesData(response.data.consumerProfiles)
        },
      },
    )
  }, [consumersForBabySitter, itemsPerPage, currentPage, requestedData])

  const handleClose = () => {
    setIsFlipped(true)
    setOpen(false)
    setIsFlipped(true)
  }

  useEffect(() => {
    getConsumerData()
  }, [getConsumerData, currentPage])

  const modalContent = (item: UserProfileInterface) => {
    return (
      <Fade in={open}>
        <Box sx={style}>
          <div className="model-close" onClick={handleClose}>
            <img src={close_icon} alt="Close" />
          </div>
          <div className="containers-popup">
            <div className="popup-heading">
              <h1>Government Issued ID</h1>
            </div>
            <img
              src={
                isFlipped
                  ? item.idFront
                    ? item.idFront
                    : '/icon/Image.svg'
                  : item.idBack
                    ? item.idBack
                    : '/icon/Image.svg'
              }
              alt={isFlipped ? 'proof-front' : 'proof-back'}
              className="id-proof"
            />
            <div className="flip-btn" onClick={handleFlip}>
              <img src={flip} alt="flip" />
            </div>
          </div>
        </Box>
      </Fade>
    )
  }

  if (isPending) {
    return <Loader />
  }

  return (
    <div>
      <div className="reg-consumer-top" onClick={handleBack}>
        <img src="/icon/Arrow left.svg" alt="Previous" /> Registered Participant
      </div>
      {consumerProfilesData.length > 0 && isSuccess ? (
        <>
          <div className="responsive-table-scroll">
            <table className="table-container bg-grad before:rounded-[20px] rounded-[20px]">
              <thead>
                <tr className="table-heading row-bg1">
                  <th className="text-left whitespace-pre border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                    Name
                  </th>
                  <th className="text-left whitespace-pre border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                    Phone Number
                  </th>
                  <th className="text-left whitespace-pre border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                    Email Address
                  </th>
                  <th className="text-left whitespace-pre border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                    Govt. ID
                  </th>
                  <th className="text-left whitespace-pre border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item, idx) => (
                  <tr
                    key={idx}
                    className={idx % 2 === 0 ? 'rowbg2' : 'row-bg1'}
                  >
                    <td className="text-left border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                      <div className="profile-cell profile-cell_container">
                        {item?.profilePic ? (
                          <img
                            alt="Profile"
                            src={item.profilePic}
                            className="consumer-avatar"
                          />
                        ) : (
                          <Avatar
                            className="!bg-[#365F91] mr-4"
                            {...stringAvatar(
                              `${item.firstName} ${item.lastName}`,
                            )}
                          />
                        )}
                        <span>
                          {item.firstName}&nbsp;{item.lastName}
                        </span>
                      </div>
                    </td>
                    <td className="text-left border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                      {formatPhoneNumber(item.phone, item.countryCode)}
                    </td>
                    <td className="text-left border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                      {item.userId.email}
                    </td>
                    <td className="border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                      <label
                        className="view-id"
                        onClick={() => handleViewProof()}
                      >
                        View ID
                      </label>
                    </td>
                    <TaskModal
                      open={open}
                      content={modalContent(item)}
                      handleClose={handleClose}
                    />
                    <td className="border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                      <Button
                        onClick={() => handleViewNavigate(item.userId._id)}
                      >
                        <img src="/icon/eye icon.png" alt="watch" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <CustomPageNavigator
            currentPage={currentPage}
            limitPerPage={itemsPerPage}
            totalItemCount={consumerData?.count || 0}
            setCurrentPage={setCurrentPage}
          />
        </>
      ) : (
        <DataUnavailable description="No record found" />
      )}
    </div>
  )
}

export default ConsumersForBabysitter
